import React, { useState } from 'react';
import { CheckCircle2Icon, ShieldCheckIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Field, Input, Textarea } from '../ui/Field';
import { ChoiceGroup } from '../ui/ChoiceGroup';
import { FeatureGate } from '../platform/FeatureStatusBadge';
import { naira } from '../../utils/format';
import type { Listing } from '../../types/housing';
const STEPS = ['Your details', 'Tenancy', 'Review'] as const;
const DURATIONS = ['One session (12 months)', 'Two sessions', 'Six months'] as const;
interface ApplicationDialogProps {
  listing: Listing;
  open: boolean;
  onClose: () => void;
}
export function ApplicationDialog({
  listing,
  open,
  onClose
}: ApplicationDialogProps) {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [moveIn, setMoveIn] = useState('2026-09-01');
  const [duration, setDuration] = useState<(typeof DURATIONS)[number]>(DURATIONS[0]);
  const [note, setNote] = useState('');
  function close() {
    onClose();
    window.setTimeout(() => {
      setStep(0);
      setSubmitted(false);
    }, 200);
  }
  if (submitted) {
    return <Modal open={open} onClose={close} title="Application sent" footer={<Button variant="primary" onClick={close} data-id="element-1753" data-mp="1k6u4lxfe5el9">
            Done
          </Button>} data-id="element-1752" data-mp="1b7ywgfssdufb">
        <div className="flex gap-3" data-id="element-1754" data-mp="ngths3yl186l">
          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-ok-700" data-id="element-1755" data-mp="nhuqw2w1fklo" />
          <div data-id="element-1756" data-mp="seuzakgd0zfy">
            <p className="text-sm leading-6 text-ink" data-id="element-1757" data-mp="sgftg8yrigda">
              {listing.manager.name} has your application for {listing.name}. You will see the status
              change on your housing page, and you will get a notification when they respond.
            </p>
            <p className="mt-3 text-[13px] leading-6 text-ink-muted" data-id="element-1758" data-mp="4qbtcbks8yr1">
              Nothing is payable yet, and never pay cash or make a personal transfer to secure a
              room.
            </p>
            <div className="mt-3" data-id="element-1759" data-mp="1c21bwyfwqvgf">
              <FeatureGate featureKey="rent_payment" data-id="element-1760" data-mp="1c19poe87aa2t" />
            </div>
          </div>
        </div>
      </Modal>;
  }
  return <Modal open={open} onClose={close} title={`Apply — ${listing.name}`} description={`Step ${step + 1} of 3 · ${STEPS[step]}`} footer={<>
          {step > 0 && <Button variant="secondary" onClick={() => setStep(value => value - 1)} data-id="element-1762" data-mp="t3p3xg0qfrmf">
              Back
            </Button>}
          {step < STEPS.length - 1 ? <Button variant="primary" onClick={() => setStep(value => value + 1)} data-id="element-1763" data-mp="o6qbd1wpvsqc">
              Continue
            </Button> : <Button variant="primary" onClick={() => setSubmitted(true)} data-id="element-1764" data-mp="y2jvb6crmoi5">
              Submit application
            </Button>}
        </>} data-id="element-1761" data-mp="171ne0980t1w4">
      <div className="mb-5 flex gap-1.5" data-id="element-1765" data-mp="2dlel0vdxawko">
        {STEPS.map((label, index) => <div key={label} className={`h-1 flex-1 rounded-full ${index <= step ? 'bg-brand-700' : 'bg-line'}`} data-id="element-1766" data-mp="29hp1ikc3v56i" data-mpi={index} />)}
      </div>

      {step === 0 && <div className="space-y-4" data-id="element-1767" data-mp="2m1vp6f2i3drs">
          <div className="rounded-lg border border-line bg-canvas px-4 py-3" data-id="element-1768" data-mp="273jsw9j1i715">
            <p className="text-[13px] font-medium text-ink" data-id="element-1769" data-mp="275o6xkankmf5">Amarachi Obi · Matric 210404021</p>
            <p className="mt-0.5 text-[12px] leading-5 text-ink-muted" data-id="element-1770" data-mp="2c4hbuobvj53p">
              B.Sc. Systems Engineering, 300 Level · University of Lagos
            </p>
          </div>
          <p className="text-[13px] leading-6 text-ink-muted" data-id="element-1771" data-mp="34qituwgrm14u">
            The operator will see your name, programme, level and student status. Your contact details
            are only released after they accept your application, in line with your privacy settings.
          </p>
          <Field label="Contact number shown to the operator" htmlFor="app-phone" data-id="element-1772" data-mp="3ips7bodl6h54">
            <Input id="app-phone" defaultValue="+234 806 555 0142" data-id="element-1773" data-mp="3nhti15v3vzt1" />
          </Field>
        </div>}

      {step === 1 && <div className="space-y-4" data-id="element-1774" data-mp="1zut8oxfov123">
          <Field label="Preferred move-in date" htmlFor="app-movein" data-id="element-1775" data-mp="1uu57xk1nr5uo">
            <Input id="app-movein" type="date" value={moveIn} onChange={event => setMoveIn(event.target.value)} data-id="element-1776" data-mp="1usu6vrzlkc79" />
          </Field>
          <ChoiceGroup label="Tenancy length" options={DURATIONS} value={duration} onChange={setDuration} data-id="element-1777" data-mp="1kxsfsqlrh021" />
          <div data-id="element-1778" data-mp="2dloa5onuyfvn">
            <label htmlFor="app-note" className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-1779" data-mp="2dl54b6bhfhxz">
              Anything the operator should know?
            </label>
            <Textarea id="app-note" rows={3} value={note} onChange={event => setNote(event.target.value)} placeholder="Room preference, whether you are applying with a roommate, quiet-hours needs." data-id="element-1780" data-mp="23vd3hucshsl2" />
          </div>
        </div>}

      {step === 2 && <div className="space-y-4" data-id="element-1781" data-mp="34ty6j0joke9f">
          <dl className="divide-y divide-line rounded-lg border border-line" data-id="element-1782" data-mp="38tgrtxfeyrcs">
            {[{
          label: 'Property',
          value: `${listing.name} — ${listing.roomType}`
        }, {
          label: 'Rent',
          value: `${naira(listing.rentPerYear)} per year`
        }, {
          label: 'Deposit',
          value: `${listing.depositMonths} month${listing.depositMonths > 1 ? 's' : ''} equivalent`
        }, {
          label: 'Move-in',
          value: moveIn
        }, {
          label: 'Tenancy',
          value: duration
        }].map(row => <div key={row.label} className="flex justify-between gap-4 px-4 py-2.5" data-id="element-1783" data-mp="g030ngn74lz">
                <dt className="text-[13px] text-ink-muted" data-id="element-1784" data-mp="5c8cbk85s3l8">{row.label}</dt>
                <dd className="cz-num text-right text-[13px] font-medium text-ink" data-id="element-1785" data-mp="ad5wu3gn59pw">{row.value}</dd>
              </div>)}
          </dl>
          <p className="flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-1786" data-mp="f8thq6o3bgpl">
            <ShieldCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-700" data-id="element-1787" data-mp="3lqbwc7sff4e6" />
            {listing.verification === 'verified' ? `Campuzon checked ${listing.verifiedChecks.join(', ').toLowerCase()} for this property. Verification is not an inspection of the room itself — visit before you commit.` : 'This property is not verified by Campuzon. Visit in person, confirm who owns it and never pay before you have seen the room.'}
          </p>
        </div>}
    </Modal>;
}